import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useAuth } from '../lib/auth-context';
import { embassyData } from '../data/embassies';
import { Building2, Phone, Mail, Globe, Clock, AlertTriangle, MapPin, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import * as amplitude from '@amplitude/analytics-browser';

export function EmbassyDirectoryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [expandedCountry, setExpandedCountry] = React.useState<string | null>(null);
  const [searchQuery, setSearchQuery] = React.useState('');


  React.useEffect(() => {
    // Analytics.
    amplitude.track("Embassy Displayed");
  }, []);

  // Redirect to home if not authenticated
  React.useEffect(() => {
    if (!user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const filteredData = React.useMemo(() => {
    if (!searchQuery) return embassyData;
    
    const query = searchQuery.toLowerCase();
    return embassyData.filter(section => 
      section.countryName.toLowerCase().includes(query) ||
      section.mainEmbassy.city.toLowerCase().includes(query) ||
      section.consulates.some(c => c.city.toLowerCase().includes(query))
    );
  }, [searchQuery]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 flex flex-col pb-mobile">
      <Navbar
        onLogin={() => {}}
        onAdminClick={() => {}}
        onHomeClick={() => {}}
        isAdminMode={false}
      />

      <main className="flex-1 pt-24 sm:pt-28 pb-12 sm:pb-16">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Title */}
          <div className="text-center mb-10 sm:mb-12">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              Ambassades et consulats français
            </h1>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              Retrouvez toutes les coordonnées des ambassades et consulats français dans les pays participants au PVT.
            </p>
          </div>
          {/* Search */}
          <div className="mb-10 sm:mb-12">
            <div className="relative max-w-2xl mx-auto">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher par pays ou ville..."
                className="w-full pl-12 pr-4 py-4 bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 rounded-2xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 dark:text-white placeholder:text-gray-400 text-base shadow-sm transition-all duration-300"
              />
            </div>
          </div>

          {/* Alert for emergency */}
          <div className="mb-10 sm:mb-12 bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/30 dark:to-orange-900/20 rounded-3xl p-6 sm:p-8 border-2 border-red-100 dark:border-red-800/40 shadow-lg">
            <div className="flex items-start gap-4 mb-4">
              <div className="p-3 bg-gradient-to-br from-red-500 to-orange-500 rounded-2xl shadow-lg flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-red-900 dark:text-red-200 pt-1">
                En cas d'urgence
              </h3>
            </div>

            <div className="space-y-3 mb-5">
              <p className="text-sm sm:text-base text-red-800 dark:text-red-300 leading-relaxed">
                En cas d'urgence absolue (accident grave, arrestation, disparition inquiétante, attentat, etc.), contactez le Centre de crise et de soutien du ministère des Affaires étrangères.
              </p>
              <p className="text-xs sm:text-sm text-red-700 dark:text-red-400 leading-relaxed">
                Ce numéro est accessible 24h/24 et 7j/7, mais il est réservé aux urgences absolues uniquement.
              </p>
            </div>

            <div className="inline-flex items-center gap-3 px-5 py-3 bg-white dark:bg-gray-800 rounded-xl shadow-md">
              <Phone className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
              <span className="text-base sm:text-lg font-bold text-red-900 dark:text-red-300">
                Centre de crise : +33 1 43 17 53 53
              </span>
            </div>
          </div>

          {/* Embassy List */}
          <div className="space-y-5 sm:space-y-6">
            {filteredData.map((section, index) => (
              <div
                key={section.countryCode}
                className="bg-white dark:bg-gray-800 rounded-3xl shadow-lg hover:shadow-xl border-2 border-gray-100 dark:border-gray-700 overflow-hidden transition-all duration-300 animate-fade-in"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <button
                  onClick={() => setExpandedCountry(
                    expandedCountry === section.countryCode ? null : section.countryCode
                  )}
                  className="w-full flex items-center justify-between p-6 sm:p-8 hover:bg-gradient-to-r hover:from-gray-50 hover:to-emerald-50/30 dark:hover:from-gray-700/50 dark:hover:to-emerald-900/20 transition-all duration-300"
                >
                  <div className="flex items-center gap-4 sm:gap-6">
                    <div className="p-3 bg-gradient-to-br from-gray-100 to-gray-50 dark:from-gray-700 dark:to-gray-600 rounded-2xl shadow-md">
                      <span className="text-3xl sm:text-4xl">{section.flag}</span>
                    </div>
                    <div className="text-left">
                      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-1">
                        {section.countryName}
                      </h2>
                      <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 font-medium">
                        {section.consulates.length + 1} représentation{section.consulates.length ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className={`p-2 rounded-xl transition-all duration-300 ${expandedCountry === section.countryCode ? 'bg-emerald-100 dark:bg-emerald-900/30' : 'bg-gray-100 dark:bg-gray-700'}`}>
                    {expandedCountry === section.countryCode ? (
                      <ChevronUp className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600 dark:text-emerald-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6 text-gray-500 dark:text-gray-400" />
                    )}
                  </div>
                </button>

                {expandedCountry === section.countryCode && (
                  <div className="border-t-2 border-gray-100 dark:border-gray-700">
                    {/* Main Embassy */}
                    <div className="p-6 sm:p-8 space-y-8 bg-gradient-to-br from-emerald-50/50 to-white dark:from-emerald-900/10 dark:to-gray-800">
                      <div className="flex items-center gap-4 mb-6">
                        <div className="p-3 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl shadow-lg">
                          <Building2 className="w-6 h-6 text-white" />
                        </div>
                        <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                          {section.mainEmbassy.name}
                        </h3>
                      </div>

                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                        <div className="space-y-5">
                          <div className="flex items-start gap-4 p-4 bg-white dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
                            <div className="p-2.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex-shrink-0">
                              <MapPin className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                            </div>
                            <div>
                              <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">
                                Adresse
                              </p>
                              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                                {section.mainEmbassy.address}
                                <br />
                                {section.mainEmbassy.city}, {section.mainEmbassy.country}
                              </p>
                            </div>
                          </div>

                          <div className="flex items-start gap-4 p-4 bg-white dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
                            <div className="p-2.5 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex-shrink-0">
                              <Phone className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                              <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">
                                Téléphone
                              </p>
                              <p className="text-sm text-gray-700 dark:text-gray-300">
                                {section.mainEmbassy.phone}
                              </p>
                              {section.mainEmbassy.emergencyNumber && (
                                <p className="text-sm text-red-600 dark:text-red-400 mt-2 font-semibold">
                                  Urgence : {section.mainEmbassy.emergencyNumber}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-start gap-4 p-4 bg-white dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
                            <div className="p-2.5 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex-shrink-0">
                              <Mail className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                            </div>
                            <div>
                              <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">
                                Email
                              </p>
                              <a
                                href={`mailto:${section.mainEmbassy.email}`}
                                className="text-sm text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 font-medium underline decoration-2 underline-offset-2 transition-colors"
                              >
                                {section.mainEmbassy.email}
                              </a>
                            </div>
                          </div>
                        </div>

                        <div className="space-y-5">
                          <div className="flex items-start gap-4 p-4 bg-white dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
                            <div className="p-2.5 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex-shrink-0">
                              <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                            </div>
                            <div>
                              <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">
                                Horaires
                              </p>
                              <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                                {section.mainEmbassy.hours.regular}
                              </p>
                              {section.mainEmbassy.hours.emergency && (
                                <p className="text-sm text-red-600 dark:text-red-400 mt-2 font-semibold">
                                  {section.mainEmbassy.hours.emergency}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-start gap-4 p-4 bg-white dark:bg-gray-800/50 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
                            <div className="p-2.5 bg-teal-100 dark:bg-teal-900/30 rounded-xl flex-shrink-0">
                              <Globe className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                            </div>
                            <div>
                              <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">
                                Site web
                              </p>
                              <a
                                href={section.mainEmbassy.website}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 font-medium transition-colors"
                              >
                                <span>Visiter le site</span>
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            </div>
                          </div>

                          {section.mainEmbassy.notes && (
                            <div className="flex items-start gap-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-2xl border-2 border-amber-200 dark:border-amber-800/40 shadow-sm">
                              <div className="p-2.5 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex-shrink-0">
                                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                              </div>
                              <div>
                                <p className="text-sm font-bold text-amber-900 dark:text-amber-300 mb-2">
                                  Remarque importante
                                </p>
                                <p className="text-sm text-amber-800 dark:text-amber-400 leading-relaxed">
                                  {section.mainEmbassy.notes}
                                </p>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Consulates */}
                    {section.consulates.map((consulate) => (
                      <div
                        key={consulate.id}
                        className="border-t-2 border-gray-200 dark:border-gray-700 p-6 sm:p-8 bg-white dark:bg-gray-800"
                      >
                        <div className="flex items-center gap-4 mb-6">
                          <div className="p-3 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-2xl shadow-lg">
                            <Building2 className="w-6 h-6 text-white" />
                          </div>
                          <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                            {consulate.name}
                          </h3>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                          <div className="space-y-5">
                            <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl border border-gray-100 dark:border-gray-600 shadow-sm">
                              <div className="p-2.5 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex-shrink-0">
                                <MapPin className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
                              </div>
                              <div>
                                <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">
                                  Adresse
                                </p>
                                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                                  {consulate.address}
                                  <br />
                                  {consulate.city}, {consulate.country}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl border border-gray-100 dark:border-gray-600 shadow-sm">
                              <div className="p-2.5 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex-shrink-0">
                                <Phone className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                              </div>
                              <div>
                                <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">
                                  Téléphone
                                </p>
                                <p className="text-sm text-gray-700 dark:text-gray-300">
                                  {consulate.phone}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl border border-gray-100 dark:border-gray-600 shadow-sm">
                              <div className="p-2.5 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex-shrink-0">
                                <Mail className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                              </div>
                              <div>
                                <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">
                                  Email
                                </p>
                                <a
                                  href={`mailto:${consulate.email}`}
                                  className="text-sm text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 font-medium underline decoration-2 underline-offset-2 transition-colors"
                                >
                                  {consulate.email}
                                </a>
                              </div>
                            </div>
                          </div>

                          <div className="space-y-5">
                            <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl border border-gray-100 dark:border-gray-600 shadow-sm">
                              <div className="p-2.5 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex-shrink-0">
                                <Clock className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                              </div>
                              <div>
                                <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">
                                  Horaires
                                </p>
                                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                                  {consulate.hours.regular}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-start gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-2xl border border-gray-100 dark:border-gray-600 shadow-sm">
                              <div className="p-2.5 bg-teal-100 dark:bg-teal-900/30 rounded-xl flex-shrink-0">
                                <Globe className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                              </div>
                              <div>
                                <p className="text-sm font-bold text-gray-900 dark:text-white mb-2">
                                  Site web
                                </p>
                                <a
                                  href={consulate.website}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300 font-medium transition-colors"
                                >
                                  <span>Visiter le site</span>
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}